import { ready } from "../core";
import { action, mount, unmount, observeClassChanges, observeMatchingSubtree } from "../actions";
import { browser } from "$lib/core/js/util/env";
import { isMobileActive } from "../stores/bodyStates";
import { derived, writable } from "../stores/svelte";
import { Logger } from "$lib/core/js/util/logger";
import { get_store_value } from "../stores/svelte/util";
import { clamp } from "$lib/core/js/util/math";

/** @type {any[]} */
let menuUnsubscribeFunctions = [];

export function initMenus() {
	if (!browser) return;
	ready(() => {
		const logger = new Logger("initMenus");
		Logger.addLogger("initMenus");

		// Clear all store subscriptions, as we are about to re-create them all if they already exist.
		for (let unsub of menuUnsubscribeFunctions) {
			unsub();
		}

		// All `<nav>` elements containing the main webpage nav items.
		const navs = [...document.querySelectorAll("nav.main")].filter(n => n instanceof HTMLElement);

		// The primary `<nav>` located in the header.
		const primaryNav = navs.find(n => n.classList.contains("primary"));

		// The mobile `<nav>`.
		const mobileNav = navs.find(n => n.classList.contains("mobile"));

		// TODO: Handle nested menu options in primary nav.
		// TODO: Handle multi-column sub-menus.

		// Menu Item Constants
		const multiColumnThreshold = 12;
		/** @type {number} The number of units that the .shift-left CSS class applies as a negative margin-left value. */
		const shiftOffset = 355;

		logger.debug({
			navs,
			primaryNav,
			mobileNav,
		});

		/**
		 * @param {HTMLElement|null} element 
		 */
		function getMenuLevel(element) {
			if (element == null) return 0;
			if (element.matches("nav")) return 0;
			if (element.matches("ul")) return Number.parseInt(element.dataset.level ?? "0");
			return getMenuLevel(element.closest("ul"));
		}

		/**
		 * 
		 * @param {HTMLElement} element 
		 */
		function getHasChildren(element) {
			return element.matches(":scope:has(li)");
		}

		for (let nav of navs) {

			logger.debug("Setting up nav", nav);

			const isMobile = () => nav.classList.contains("mobile");

			// Reactive Stores
			const selectedItemStack = writable(/** @type {Array<HTMLLIElement>} */ ([]));
			const listItems = writable(/** @type {HTMLLIElement[]} */ ([...nav.querySelectorAll(":scope li")])); // All list items within THIS nav element.
			const menuLevel = writable(0); // Current level of the menu. Currently, this value only changes for the mobile menu.
			const menuState = derived([menuLevel, selectedItemStack], (([level, stack]) => {
				return { level, stack };
			}));

			/**
			 * @param {Event} event
			 */
			function onBackButtonClicked(event) {
				const ev = /** @type {MouseEvent|KeyboardEvent} */ (event);
				logger.debug("Back button clicked");
				ev.stopImmediatePropagation();
				ev.preventDefault();

				menuLevel.update(v => clamp(v - 1, 0, 1000));
				logger.debug("menu level: ", get_store_value(menuLevel));
				if (get_store_value(selectedItemStack).length > 0) {
					selectedItemStack.update(arr => {
						arr.pop();
						logger.debug("popped mobile menu item stack", arr);
						return arr;
					})
				}
			}

			action(nav, observeMatchingSubtree(listItems, "li", (record) => { // 
				record.added.forEach(mount); // Triggers actions associated with menu items when they are dynamically added to the page.
				record.removed.forEach(unmount); // Triggers cleanup operations if actions are attached to <li> elements removed from the page.
			}));

			if (isMobile()) {
				for (let btn of nav.querySelectorAll(":scope button[data-back-btn='true']")) {
					btn.addEventListener("click", onBackButtonClicked);
				}
				for (let btn of nav.querySelectorAll(":scope mobile-back-button")) {
					btn.addEventListener("click", onBackButtonClicked);
				}
			}

			get_store_value(listItems).forEach(item => {
				action(item, initListItem);

				mount(item);
			});

			/**
			 * @param {HTMLLIElement|undefined} item 
			 */
			function initListItem(item) {
				if (item == null) {
					logger.debug("initializing undefined or null item!");
					return;
				}

				const hasChildren = getHasChildren(item);

				const level = getMenuLevel(item);

				/**
				 * @param {MouseEvent|KeyboardEvent} ev 
				 */
				function onItemClick(ev) {
					const listItem = /** @type {HTMLLIElement} */ (item);
					logger.debug("item clicked [START]", listItem);
					let mobileActive = false;
					if (isMobile()) {
						if (hasChildren) {
							ev.stopImmediatePropagation();
							ev.preventDefault();
							mobileActive = true;
							menuLevel.update(v => v + 1);
							selectedItemStack.update(arr => {
								arr.push(listItem);
								return arr;
							});
							logger.debug("item clicked (2)", { listItem, hasChildren, level });
						} else {
							ev.stopPropagation();
						}
					}

					// Not sure why we do this check honestly. Probably not implemented/tested properly anyway.
					// If a link goes to the same page, but with a fragment reference, then force-close the mobile menu.
					const anchor = /** @type {HTMLAnchorElement} */ (listItem.querySelector(":scope a"));
					const currentPageUrl = new URL(location.href);
					const currentPage = currentPageUrl.search;
					if(currentPage.length > 0 && anchor.href.includes(currentPage) && anchor.href.includes('#')) {
						mobileActive = false;
					}

					isMobileActive.set(mobileActive);
				}

				const stateUnsubscriber = menuState.subscribe((state) => {
					const { level: currentMenuLevel, stack } = state;
					const showChildren = (currentMenuLevel > level && item && stack.includes(item));
					item.classList.toggle("show_children", showChildren);

					const hidden = currentMenuLevel <= level;
					const block = item.querySelector(":scope > div:last-of-type");

					if (block) {
						logger.debug("<li> knows that menu state updated: ", state, { item, showChildren, hidden, block });

						block.classList.toggle("hidden", hidden);
					}
				});

				item.addEventListener("click", onItemClick);

				logger.debug({ item, hasChildren, level });

				return () => {
					stateUnsubscriber();
					item.removeEventListener("click", onItemClick);
				}
			}

			listItems.update(v => v); // Trigger "subscribe" actions once during startup to initialize things.
		}

		// Unmount and then re-mount all navs
		navs.forEach(unmount);
		navs.forEach(mount);
	})
}
